'use client'

import { Button } from '@/components/ui/button'

import { useEffect } from 'react'

import * as Sentry from '@sentry/nextjs'
import { TriangleAlert } from 'lucide-react'

/**
 * Root segment error boundary — the styled backstop for server errors the
 * dedicated paths don't intercept (DB failures deep in a layout after a
 * cookie-cached session, unexpected render errors). Production strips
 * error messages down to a digest, so the copy stays generic; the digest
 * is shown small for support correlation. Known-infrastructure failures
 * at the session/permission chokepoints never reach here — they redirect
 * to /db-unavailable instead.
 */
export default function RootError({
  error,
  reset
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-6">
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <div className="bg-muted flex size-14 items-center justify-center rounded-full">
          <TriangleAlert className="text-muted-foreground size-7" />
        </div>
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-muted-foreground text-sm">
          Atlas hit an unexpected error serving this page. It&apos;s been
          reported automatically — retrying is usually enough.
        </p>
        {process.env.NODE_ENV === 'development' && (
          <p className="text-muted-foreground border-border-strong bg-muted/50 max-w-full overflow-x-auto rounded-md border border-dashed px-3 py-2 text-left font-mono text-xs whitespace-pre-wrap">
            {error.message}
          </p>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={() => reset()}>Try again</Button>
          <Button variant="outline" asChild>
            {/* Full document load on purpose — after an error boundary
                trips, a clean top-level request beats client-side nav on
                possibly-poisoned state. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/">Back to Atlas</a>
          </Button>
        </div>
        {error.digest && (
          <span className="text-muted-foreground/70 text-xs">
            Reference: {error.digest}
          </span>
        )}
      </div>
    </div>
  )
}
